/**
 * A small bar chart, drawn as inline SVG.
 *
 * Deliberately not a charting library. Two charts do not justify a megabyte of
 * JavaScript in a console people open on a laptop over a VPN at 03:00, and a
 * dependency in the render path of the screen that tells you whether you are under
 * attack is a dependency worth not having. When a real dashboard needs zooming,
 * brushing and cross-filtering, that is the moment to reconsider — not before.
 *
 * The chart is not the only representation of the data: the numbers are in the
 * accessible summary underneath, so it is readable without seeing it.
 */
export function Sparkbars({
  points,
  label,
}: {
  points: { hour: string; alerts: number }[]
  label: string
}) {
  const peak = Math.max(1, ...points.map((point) => point.alerts))
  const total = points.reduce((sum, point) => sum + point.alerts, 0)
  const width = 100
  const height = 28
  const barWidth = width / Math.max(points.length, 1)

  return (
    <figure style={{ margin: 0 }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: 60 }}
        role="img"
        aria-label={`${label}: ${total} in the last ${points.length} hours, busiest hour ${peak}`}
      >
        {points.map((point, index) => {
          const barHeight = (point.alerts / peak) * height
          return (
            <rect
              key={point.hour}
              x={index * barWidth}
              y={height - barHeight}
              width={Math.max(barWidth - 0.6, 0.4)}
              height={barHeight}
              fill="var(--accent)"
            >
              <title>
                {new Date(point.hour).toLocaleTimeString([], { hour: '2-digit' })}:{' '}
                {point.alerts}
              </title>
            </rect>
          )
        })}
      </svg>
      <figcaption className="muted" style={{ fontSize: 'var(--text-xs)' }}>
        {label} — {total} in {points.length}h, peak {peak}/h
      </figcaption>
    </figure>
  )
}
